import { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { fetchPublic, type PublicApiResult } from '@/lib/publicApiClient';

type ResultState = {
  name: string;
  path: string;
  result?: PublicApiResult<unknown>;
};

const ENDPOINTS: ResultState[] = [
  { name: 'profiles', path: 'profiles?select=id,handle,display_name&limit=5' },
  { name: 'public_leaderboard_global_v1', path: 'public_leaderboard_global_v1?select=*' },
  { name: 'public_leaderboard_gym_v1', path: 'public_leaderboard_gym_v1?select=*' },
];

export default function SmokePublicScreen() {
  const router = useRouter();
  const { theme } = useTheme();
  const [results, setResults] = useState<ResultState[]>(ENDPOINTS);
  const [running, setRunning] = useState(false);

  // V1: Lock dev routes in production
  useEffect(() => {
    if (!__DEV__) { router.replace('/'); }
  }, [router]);
  if (!__DEV__) return null;

  const runTests = async () => {
    setRunning(true);
    const next: ResultState[] = [];
    for (const endpoint of ENDPOINTS) {
      const result = await fetchPublic<unknown>(endpoint.path);
      next.push({ ...endpoint, result });
    }
    setResults(next);
    setRunning(false);
  };

  const renderRow = (item: ResultState) => {
    const res = item.result;
    const dataArray = Array.isArray(res?.data) ? (res?.data as unknown[]) : null;
    const rowCount = dataArray ? dataArray.length : undefined;
    const firstItem =
      dataArray && dataArray.length > 0
        ? JSON.stringify(dataArray[0]).slice(0, 160)
        : undefined;
    const statusText = res ? res.status.toString() : '—';
    const okText = res ? (res.ok ? 'ok' : 'fail') : '—';

    return (
      <View key={item.name} style={[styles.card, { backgroundColor: theme.colors.surface }]}>
        <Text style={[styles.endpoint, { color: theme.colors.text }]}>{item.name}</Text>
        <Text style={[styles.detail, { color: theme.colors.textSecondary }]}>
          status: {statusText} • {okText}
        </Text>
        {typeof rowCount === 'number' && (
          <Text style={[styles.detail, { color: theme.colors.textSecondary }]}>
            rows: {rowCount}
          </Text>
        )}
        {firstItem && (
          <Text style={[styles.preview, { color: theme.colors.textSecondary }]}>
            first: {firstItem}
          </Text>
        )}
        {res?.error && (
          <Text style={[styles.error, { color: theme.colors.error }]}>
            error: {res.error}
          </Text>
        )}
      </View>
    );
  };

  return (
    <ScrollView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.header}>
        <Text style={[styles.title, { color: theme.colors.text }]}>
          Public Views Smoke Test
        </Text>
        <Text style={[styles.banner, { color: theme.colors.textSecondary }]}>
          Requires EXPO_PUBLIC_POSTGREST_URL, EXPO_PUBLIC_SUPABASE_ANON_KEY,
          EXPO_PUBLIC_POSTGREST_JWT
        </Text>
        <TouchableOpacity
          style={[styles.button, { backgroundColor: theme.colors.accent }]}
          onPress={runTests}
          disabled={running}>
          <Text style={[styles.buttonText, { color: theme.colors.text }]}>
            {running ? 'Running…' : 'Run tests'}
          </Text>
        </TouchableOpacity>
      </View>

      <View style={styles.results}>{results.map(renderRow)}</View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    padding: 16,
    gap: 10,
  },
  title: {
    fontSize: 20,
    fontWeight: '800',
  },
  banner: {
    fontSize: 12,
    lineHeight: 16,
  },
  button: {
    paddingVertical: 12,
    borderRadius: 2,
    alignItems: 'center',
  },
  buttonText: {
    fontSize: 14,
    fontWeight: '700',
  },
  results: {
    paddingHorizontal: 16,
    paddingBottom: 24,
    gap: 12,
  },
  card: {
    borderRadius: 2,
    padding: 12,
    gap: 6,
  },
  endpoint: {
    fontSize: 14,
    fontWeight: '700',
  },
  detail: {
    fontSize: 12,
  },
  preview: {
    fontSize: 12,
  },
  error: {
    fontSize: 12,
  },
});
